import { getConfig } from '@/utils/config/config';
import { buildApiUrl } from '@/utils/url/build-url';
import { getCookieValue, COOKIES } from '@/utils/cookies';
import { LOCALES } from '@/utils/constants';
import type { Locale, PrismicLocaleCode } from '@/types/constants';

export type SearchHit = {
    _id: string;
    _source?: unknown;
};

export type SearchHitsPayload = {
    total: {
        value: number;
    };
    hits: SearchHit[];
};

export type SearchHits = SearchHitsPayload & {
    searchEngine: string;
    searchEngineVersion: string;
};

export type SearchResponse = {
    body: {
        hits: SearchHitsPayload;
    };
    searchEngine: string;
    searchEngineVersion: string;
};

export type SearchOptions = {
    /** Zero-based page index. Passed as offset to the API (pageIndex * pageSize). */
    pageIndex?: number;
    /** Number of results per page. Default 10 (desktop). Use 20 for mobile/tablet. */
    pageSize?: number;
    /** Amplitude expermient value: variant/control */
    experimentGroupId?: string;
    /** Snowplow client_session.sessionId (domain_sessionid). */
    sessionId?: string;
    /** Passed to the lambda as x-request-id when calling the ML search API. */
    requestId?: string;
};

const DEFAULT_PAGE_INDEX = 0;
const DEFAULT_PAGE_SIZE = 10;

const getParams = async (query: string, prismicLocale?: PrismicLocaleCode, options: SearchOptions = {}) => {
    const config = getConfig();

    const [platform, region, authToken, loginToken] = await Promise.all([
        getCookieValue(COOKIES.PLATFORM),
        getCookieValue(COOKIES.REGION),
        getCookieValue(COOKIES.AUTH_TOKEN),
        getCookieValue(COOKIES.LOGIN_TOKEN)
    ]);

    const regionKey = region?.toUpperCase();
    const regionLocale = regionKey && regionKey in LOCALES ? LOCALES[regionKey as Locale] : undefined;
    const locale: string = regionLocale ?? prismicLocale ?? Object.values(LOCALES)[0];

    const host = buildApiUrl(config.apis.CasinoSearchClient);

    const headers: Record<string, string> = {
        'x-api-key': config.apis.CasinoSearchClient.headers?.['x-api-key'] ?? '',
        'X-Auth-Token': authToken ?? '',
        'X-Login-Token': loginToken ?? ''
    };

    const pageIndex = options.pageIndex ?? DEFAULT_PAGE_INDEX;
    const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
    const from = pageIndex * pageSize;
    const experimentGroupId = options.experimentGroupId ?? '';
    const sessionId = options.sessionId ?? '';
    const requestId = options.requestId ?? crypto.randomUUID();

    const normalizedQuery = query.trim();
    const searchParams = new URLSearchParams({
        product: process.env.PRODUCT as string,
        platform,
        query: normalizedQuery,
        from: String(from),
        size: String(pageSize),
        locale,
        experiment_group_id: experimentGroupId,
        session_id: sessionId,
        request_id: requestId
    });

    return { host, headers, searchParams };
};

export const searchGames = async (
    query: string,
    prismicLocale?: PrismicLocaleCode,
    options: SearchOptions = {}
): Promise<SearchHits | null> => {
    const { host, headers, searchParams } = await getParams(query, prismicLocale, options);

    try {
        const res = await fetch(`${host}?${searchParams}`, {
            method: 'GET',
            headers
        });

        if (!res.ok) {
            return null;
        }

        const data: SearchResponse = await res.json();

        return {
            ...data.body.hits,
            searchEngine: data.searchEngine,
            searchEngineVersion: data.searchEngineVersion
        };
    } catch (error) {
        console.error(`(searchGames) Error searching games: ${(error as Error).message}`);
        return null;
    }
};
